/**
 * A state indicate the selection.
 * Multiple options can be combined.
 **/
export enum SelectState {
	NONE = 0x0,
	FIRST = 0x1,
	MIDDLE = 0x2, // TODO: rename to some?
	LAST = 0x4,
	ALL = 0x8 + FIRST + MIDDLE + LAST // All implies all others, but not the other way around.
}

// @Deprecated: use ListInfo instead.
export type ListItem<T> = {
	index: number;
	value: T;
};

type ListRowInfo = {
	visible: boolean;
	selected: boolean;
	enabled: boolean;
};

const DEFAULT_INFO: ListRowInfo = {
	visible: true,
	selected: false,
	enabled: true
};

// Keeps track of all rows that every existed!
// This means the key must be unique and should not be added multiple times.
export class ListInfo<T> {
	// The map keeps track of all key to info.
	// This also includes deleted elements.
	private readonly rows: Map<T, ListRowInfo>;

	// The keys should only contain the existing keys.
	private readonly keys: T[];

	public static init<T>(): ListInfo<T> {
		const rows = new Map<T, ListRowInfo>();
		const keys: T[] = [];
		return new ListInfo<T>(rows, keys);
	}

	private constructor(rows: Map<T, ListRowInfo>, keys: T[]) {
		this.rows = rows;
		this.keys = keys;
	}

	private getInfo(key: T): ListRowInfo | undefined {
		return this.rows.get(key);
	}

	public isVisible(key: T): boolean {
		return this.getInfo(key)?.visible ?? false;
	}

	public isSelected(key: T): boolean {
		const info = this.getInfo(key);
		return (info?.enabled && info?.selected) ?? false;
	}

	public isEnabled(key: T): boolean {
		return this.getInfo(key)?.enabled ?? false;
	}

	public isVisibleAndSelected(key: T): boolean {
		const info = this.getInfo(key);
		return (
			info !== undefined && info.visible && info.enabled && info.selected
		);
	}

	public isVisibleAndEnabled(key: T): boolean {
		const info = this.getInfo(key);
		return info !== undefined && info.visible && info.enabled;
	}

	public getVisibleKeys(): T[] {
		return this.keys.filter(key => this.isVisible(key));
	}

	public getVisibleAndSelectedKeys(): T[] {
		return this.keys.filter(key => this.isVisibleAndSelected(key));
	}

	public getVisibleAndEnabledKeys(): T[] {
		return this.keys.filter(key => this.isVisibleAndEnabled(key));
	}

	public allVisibleSelected(): boolean {
		const info = this.keys
			.map(key => this.getInfo(key))
			.filter(info => info?.enabled === true);
		const visible = info.filter(info => info?.visible === true);
		const selected = visible.filter(info => info?.selected === true);
		return selected.length > 0 && selected.length === visible.length;
	}

	public someVisibleSelected(): boolean {
		const info = this.keys
			.map(key => this.getInfo(key))
			.filter(info => info?.enabled === true);
		const visible = info.filter(info => info?.visible === true);
		const selected = visible.filter(info => info?.selected === true);
		return selected.length > 0 && selected.length < visible.length;
	}

	public noVisibleSelected(): boolean {
		const info = this.keys
			.map(key => this.getInfo(key))
			.filter(info => info?.enabled === true);
		const visible = info.filter(info => info?.visible === true);
		const selected = visible.filter(info => info?.selected === true);
		return selected.length === 0;
	}

	// Marks all specified keys as being selected.
	// All other keys are not being modified.
	public markSelected(keys: T[], selected: boolean): ListInfo<T> {
		keys.forEach(key => {
			const current = this.getInfo(key);
			if (current !== undefined) {
				this.rows.set(key, {
					...current,
					selected: selected
				});
			}
		});

		return new ListInfo(this.rows, this.keys);
	}

	// Marks all specified keys as being visible.
	// All other keys are not being modified.
	public markVisible(keys: T[], visible: boolean): ListInfo<T> {
		keys.forEach(key => {
			const current = this.getInfo(key);
			if (current !== undefined) {
				this.rows.set(key, {
					...current,
					visible: visible
				});
			}
		});

		return new ListInfo(this.rows, this.keys);
	}

	// Sets all specified keys as visible, all others are hidden.
	public setVisible(keys: T[]): ListInfo<T> {
		const newInfo = new Map<T, ListRowInfo>();
		this.rows.forEach((entry, key) => {
			newInfo.set(key, {
				...entry,
				visible: keys.includes(key)
			});
		});

		return new ListInfo(newInfo, this.keys);
	}

	// Sets all specified keys as selected, all others are unselected.
	public setSelected(keys: T[]): ListInfo<T> {
		const newInfo = new Map<T, ListRowInfo>();
		this.rows.forEach((entry, key) => {
			newInfo.set(key, {
				...entry,
				selected: keys.includes(key)
			});
		});

		return new ListInfo(newInfo, this.keys);
	}

	// Set all specified keys as enabled, all others are disabled.
	public setEnabled(keys: T[]): ListInfo<T> {
		const newInfo = new Map<T, ListRowInfo>();
		this.rows.forEach((entry, key) => {
			newInfo.set(key, {
				...entry,
				enabled: keys.includes(key)
			});
		});

		return new ListInfo(newInfo, this.keys);
	}

	// Updates the current keys to the provided list.
	// This will erase the previous list of keys.
	public withKeys(keys: T[]): ListInfo<T> {
		for (const key of keys) {
			if (!this.rows.has(key)) {
				this.rows.set(key, DEFAULT_INFO);
			}
		}

		return new ListInfo(this.rows, keys);
	}

	// @Deprecated?
	public toSelectState(): SelectState {
		let state = SelectState.NONE;
		let selectCount = 0;
		const info = this.keys.map(key => this.getInfo(key));
		const visible = info.filter(info => info?.visible === true);
		for (const [index, row] of visible.entries()) {
			const selected = row?.selected ?? false;
			if (!selected) {
				continue;
			}

			++selectCount;
			if (index === 0) {
				state |= SelectState.FIRST;
			}
			if (index === visible.length - 1) {
				state |= SelectState.LAST;
			}
			if (index > 0 && index < visible.length - 1) {
				state |= SelectState.MIDDLE;
			}
		}

		if (selectCount > 0 && selectCount === visible.length) {
			return SelectState.ALL;
		} else {
			return state;
		}
	}
}
